import {
    Cloud,
    CloudFog,
    CloudRain,
    CloudSnow,
    CloudSun,
    Sun,
    Zap,
} from 'lucide-react';
import { useWeather } from '../hooks/useWeather';

function WeatherGlyph({ code, className }) {
    if (code === 0) return <Sun className={className} />;
    if (code === 1) return <Sun className={className} />;
    if (code === 2) return <CloudSun className={className} />;
    if (code === 3) return <Cloud className={className} />;
    if (code === 45 || code === 48) return <CloudFog className={className} />;
    if (code >= 51 && code <= 67) return <CloudRain className={className} />;
    if (code >= 71 && code <= 77) return <CloudSnow className={className} />;
    if (code >= 80 && code <= 82) return <CloudRain className={className} />;
    if (code >= 95 && code <= 99) return <Zap className={className} />;
    return <Cloud className={className} />;
}

function formatTime(d) {
    return d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
}

export default function Weather() {
    const { snapshot, loading, error } = useWeather();

    return (
        <div className="bg-white rounded-3xl p-6 border border-gray-200 h-fit">
            <div className="flex items-center justify-between gap-2 mb-6 text-gray-400">
                <div className="flex items-center gap-2 min-w-0">
                    <Cloud size={18} className="shrink-0" />
                    <span className="text-[11px] font-bold uppercase tracking-widest truncate">
                        Pogoda Żywocice
                    </span>
                </div>
                {snapshot && (
                    <span className="text-[10px] text-gray-400 tabular-nums shrink-0" title="Ostatnie odświeżenie">
                        {formatTime(snapshot.updatedAt)}
                    </span>
                )}
            </div>

            {error && !snapshot && (
                <p className="text-sm text-red-600 mb-4">{error}</p>
            )}

            <div className="flex items-center gap-3 mb-6 flex-wrap">
                <span
                    className={`text-5xl font-light tracking-tighter text-gray-900 tabular-nums leading-none ${
                        loading && !snapshot ? 'animate-pulse' : ''
                    }`}
                >
                    {snapshot != null ? `${snapshot.tempC}°C` : loading ? '—°C' : '—°C'}
                </span>
                <span className="flex shrink-0 items-center justify-center text-sky-600" aria-hidden>
                    {snapshot != null ? (
                        <WeatherGlyph code={snapshot.weatherCode} className="w-11 h-11 sm:w-12 sm:h-12" />
                    ) : (
                        <Cloud
                            className={`w-11 h-11 sm:w-12 sm:h-12 text-gray-300 ${
                                loading ? 'animate-pulse' : ''
                            }`}
                            strokeWidth={1.25}
                        />
                    )}
                </span>
                <span
                    className={`text-sm text-gray-400 italic font-medium max-w-[12rem] leading-snug ${
                        loading && !snapshot ? 'animate-pulse' : ''
                    }`}
                >
                    {snapshot?.labelPl ?? (loading ? '…' : '—')}
                </span>
            </div>

            <div className="space-y-4 pt-6 border-t border-gray-100">
                <div className="flex justify-between text-sm">
                    <span className="text-gray-400 font-medium">Wiatr</span>
                    <span className="font-bold text-gray-700 tabular-nums text-right">
                        {snapshot?.windKmh != null
                            ? `${snapshot.windKmh} km/h ${snapshot.windDir}`
                            : '—'}
                    </span>
                </div>
                <div className="flex justify-between text-sm">
                    <span className="text-gray-400 font-medium">Porywy</span>
                    <span className="font-bold text-gray-700 tabular-nums text-right">
                        {snapshot?.windGustKmh != null
                            ? `${snapshot.windGustKmh} km/h ${snapshot.windDir}`
                            : '—'}
                    </span>
                </div>
                <div className="flex justify-between text-sm">
                    <span className="text-gray-400 font-medium">Opady (1h)</span>
                    <span className="font-bold text-gray-700 tabular-nums">
                        {snapshot?.precipitationMm != null
                            ? snapshot.precipitationMm > 0
                                ? `${snapshot.precipitationMm} mm`
                                : '0 mm'
                            : '—'}
                    </span>
                </div>
                <div className="flex justify-between text-sm">
                    <span className="text-gray-400 font-medium">Wilgotność</span>
                    <span className="font-bold text-gray-700 tabular-nums">
                        {snapshot != null ? `${snapshot.humidityPct}%` : '—'}
                    </span>
                </div>
            </div>
        </div>
    );
}
